import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import Avatar from "@/components/Avatar";
import { BadgeGrid } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, LogOut, TrendingUp, Trophy, Users, Target, Sparkles } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line, Legend } from "recharts";

const SECTION_LABEL = { quantitative: "كمي", verbal: "لفظي" };

export default function ParentPerformance() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [p, setP] = useState(null);

    useEffect(() => { api.get("/parent/performance").then((r) => setP(r.data)).catch(() => {}); }, []);

    if (!p) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">جاري التحميل...</div>;

    const compareData = [
        { name: "قدرات كمية", "درجات ابنتي": p.my_quantitative_avg, "متوسط الفصل": p.class_quantitative_avg },
        { name: "قدرات لفظية", "درجات ابنتي": p.my_verbal_avg, "متوسط الفصل": p.class_verbal_avg },
    ];
    const timeline = p.timeline.map((t, i) => ({
        idx: i + 1, النتيجة: t.score, section: SECTION_LABEL[t.section], title: t.topic_title,
    }));
    const overallAvg = Math.round(((p.my_quantitative_avg || 0) + (p.my_verbal_avg || 0)) / 2);

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50">
            <header className="bg-[#0F172A] text-white sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={() => nav("/parent")} data-testid="btn-back-parent" className="text-white/80 hover:text-white hover:bg-white/10"><ArrowRight className="w-4 h-4 ml-1" /> الرئيسية</Button>
                        <BrandLogo size={36} />
                    </div>
                    <div className="flex items-center gap-2">
                        <Avatar url={user?.avatar_url} name={user?.full_name} seed={user?.avatar_seed} role={user?.role} size={32} />
                        <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }} className="text-white/80 hover:text-white hover:bg-white/10"><LogOut className="w-4 h-4" /></Button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto p-6 space-y-6">
                <div>
                    <h1 className="text-2xl font-bold text-[#0F172A]">أداء {p.student?.full_name}</h1>
                    <p className="text-sm text-slate-500 mt-1">تابعي تقدّم ابنتكِ في اختبارات القدرات ومقارنتها بمتوسط الفصل</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 text-[#1B2A4E] flex items-center justify-center mb-3"><Target className="w-5 h-5" /></div>
                        <div className="text-3xl font-bold text-[#0F172A] font-mono-num" data-testid="stat-parent-overall">{overallAvg}%</div>
                        <div className="text-xs text-slate-500 mt-1">المتوسط العام</div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 text-[#1B2A4E] flex items-center justify-center mb-3"><Trophy className="w-5 h-5" /></div>
                        <div className="text-3xl font-bold text-[#0F172A] font-mono-num">{p.my_quantitative_avg}%</div>
                        <div className="text-xs text-slate-500 mt-1">قدرات كمية</div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <div className="w-10 h-10 rounded-lg bg-[#FFF1F2] text-[#C8102E] flex items-center justify-center mb-3"><Trophy className="w-5 h-5" /></div>
                        <div className="text-3xl font-bold text-[#0F172A] font-mono-num">{p.my_verbal_avg}%</div>
                        <div className="text-xs text-slate-500 mt-1">قدرات لفظية</div>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3"><TrendingUp className="w-5 h-5" /></div>
                        <div className="text-3xl font-bold text-[#0F172A] font-mono-num">{p.my_total_attempts}</div>
                        <div className="text-xs text-slate-500 mt-1">اختبارات محلولة</div>
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <div className="flex items-center gap-2 mb-4"><Sparkles className="w-4 h-4 text-[#F59E0B]" /><h3 className="font-semibold text-[#0F172A]">الشارات المكتسبة</h3></div>
                    <BadgeGrid badges={p.badges || []} emptyText="لم تحصل ابنتكِ على شارات بعد" />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <div className="flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-[#1B2A4E]" /><h3 className="font-semibold text-[#0F172A]">ابنتكِ مقابل الفصل</h3></div>
                        <div className="w-full h-64">
                            <ResponsiveContainer>
                                <BarChart data={compareData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                                    <Tooltip contentStyle={{ direction: "rtl", fontSize: 12 }} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Bar dataKey="درجات ابنتي" fill="#1B2A4E" radius={[6, 6, 0, 0]} />
                                    <Bar dataKey="متوسط الفصل" fill="#C8102E" radius={[6, 6, 0, 0]} opacity={0.75} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4 text-emerald-600" /><h3 className="font-semibold text-[#0F172A]">التطور عبر آخر الاختبارات</h3></div>
                        {timeline.length === 0 ? (
                            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">لم تحلّ ابنتكِ أي اختبار بعد</div>
                        ) : (
                            <div className="w-full h-64">
                                <ResponsiveContainer>
                                    <LineChart data={timeline} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="idx" tick={{ fontSize: 11 }} />
                                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                                        <Tooltip contentStyle={{ direction: "rtl", fontSize: 12 }} labelFormatter={(i) => `الاختبار ${i}`} />
                                        <Line type="monotone" dataKey="النتيجة" stroke="#1B2A4E" strokeWidth={2.5} dot={{ r: 4, fill: "#C8102E" }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <h3 className="font-semibold text-[#0F172A] mb-3">تفصيل الاختبارات</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                                <tr>
                                    <th className="px-3 py-2 text-right">الموضوع</th>
                                    <th className="px-3 py-2 text-right">القسم</th>
                                    <th className="px-3 py-2 text-right">درجتها</th>
                                    <th className="px-3 py-2 text-right">متوسط الفصل</th>
                                    <th className="px-3 py-2 text-right"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {p.per_topic.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400">لا توجد بيانات بعد</td></tr>}
                                {p.per_topic.map((r) => {
                                    const diff = Math.round(r.my_score - r.class_avg);
                                    return (
                                        <tr key={r.topic_id} className="border-t border-slate-100">
                                            <td className="px-3 py-2 font-medium">{r.title}</td>
                                            <td className="px-3 py-2"><Badge variant="outline">{SECTION_LABEL[r.section]}</Badge></td>
                                            <td className="px-3 py-2 font-mono-num font-bold">{r.my_score}%</td>
                                            <td className="px-3 py-2 font-mono-num text-slate-500">{r.class_avg}%</td>
                                            <td className="px-3 py-2 text-xs font-mono-num" dir="ltr">
                                                {diff > 0 ? <span className="text-emerald-600">+{diff}%</span> : diff < 0 ? <span className="text-[#C8102E]">{diff}%</span> : <span className="text-slate-400">=</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
}
